<template>
  <BoxWithTitle
    collapsible
    openingIcon="mdi-chevron-down"
    closingIcon="mdi-chevron-up"
  >
    <template #header>
      <v-toolbar-title>
        {{ $t('automaticSentences.pageName') }}
      </v-toolbar-title>
    </template>
    <template #content>
      <VTableRemote
        ref="tableRemoteRef"
        :iconHeader="hasPermission('automaticSentences.sentences.add') ? 'add' : null"
        :pagination="pagination"
        :items="displayedSentences"
        isSearchable
        :switchFilters="switchFilters"
        :headers="tableHeaders"
        :isCustomIcon="true"
        :itemActions="actions"
        :sortableColumns="['title', 'category']"
        :title="$t('automaticSentences.pageName')"
        :centerColumns="['showInComments']"
        :notSortableColumns="['showInComments']"
        isInfinitScroll
        hideDefaultFooter
        @iconAction="onClickAction"
        @sendQueryParams="fetchQuery($event, { forceFetching: true, override: true }, 1)"
        @change-switch-filters="onChangeSwitchFilters"
      >
        <template #item.showInComments="{item}">
          <div class="checkbox">
            <VCheckbox
              :readonly="true"
              :value="item.showInComments"
              class="d-flex justify-center"
            />
          </div>
        </template>
        <template #withInfiniteScroll>
          <div v-intersect="endIntersect" />
        </template>
      </VTableRemote>
    </template>
  </BoxWithTitle>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import { cloneDeep, isPlainObject, isEmpty } from 'lodash';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm';
import AutomaticSentenceForm from '@/components/forms/AutomaticSentenceForm.vue';
import BoxWithTitle from '@/components/shared/containers/VBoxWithTitle';
import VTableRemote from '@/components/shared/content-views/VTableRemote';
import VCheckbox from '@/components/shared/widgets/forms/VuetifyInputs/VCheckbox.vue';
import perpage from '@/constants/pagination';

export default {
    components: {
        BoxWithTitle,
        VTableRemote,
        VCheckbox,
    },
    data() {
        return {
            sentences: [],
            pagination: {},
            queryParams: {
                perpage,
                page: 1,
                pagination: true,
                order: null,
            },
        };
    },
    computed: {
        ...mapGetters([
            'hasPermission',
            'userLang',
            'lang',
            'teamMainLang',
            'selectedLang',
        ]),
        tableHeaders() {
            return [
                'title',
                'category',
                'analyses',
                'components',
                'showInComments',
            ].map(header => ({
                text: this.$t(`forms.labels.${header}`),
                value: header,
            }));
        },
        displayedSentences() {
            if (this.sentences) {
                return this.sentences?.map(sentence => {
                    const result = {
                        ...sentence,
                        title: this.displayedText(sentence.title),
                        sentence: this.displayedText(sentence.sentence),
                        analyses: sentence.analysisTypes.map(({ name }) => this.displayedText(name)),
                        components: sentence.componentTypes.map(({ name }) => this.displayedText(name)),
                    };

                    if (isPlainObject(sentence.category)) {
                        result.category = sentence.category.name[this.userLang];
                    }

                    return result;
                });
            }
            return [];
        },
        actions() {
            const actions = [];

            if (this.hasPermission('automaticSentences.sentences.edit')) {
                actions.push({
                    name: this.$t('forms.btn.edit'),
                    icon: 'edit',
                });
            }

            if (this.hasPermission('automaticSentences.sentences.remove')) {
                actions.push({
                    name: this.$t('forms.btn.delete'),
                    icon: 'delete',
                    iconAndTextColor: '#EF4135',
                });
            }


            return actions;
        },
        switchFilters() {
            return [{
                label: this.$t('clients.labels.includeInactive'),
                value: false,
            }];
        },
    },
    async created() {
        await this.getSentences();
    },
    methods: {
        async getSentences() {
            this.isFetching = true;
            const cleanQueryParams = this.$utils.removeEmptyNullOrUndefined(this.queryParams, true);
            if (isEmpty(this.pagination) || cleanQueryParams.page === 1 || (this.pagination?.currentPage !== this.queryParams?.page)) {
                const { data } = await this.$rq.getAutomaticSentences(cleanQueryParams);
                this.sentences = [...this.sentences, ...data.sentences];
                this.pagination = data.pagination;
            }
            this.isFetching = false;
        },
        async fetchQuery(queryParams) {
            this.queryParams = {
                ...this.queryParams,
                ...queryParams,
                page: 1,
            };
            this.resetPaginationVariables();
            await this.getSentences();
        },
        onClickAction(obj, action) {
            if (obj === 'add') {
                this.$root.$emit('showModal', {
                    title: this.$t('automaticSentences.addAutomaticSentence'),
                    component: Vue.component('AutomaticSentenceForm', AutomaticSentenceForm),
                    showLangs: true,
                    isForm: true,
                    onConfirm: requestPayload => this.addAutomaticSentence(requestPayload),
                });
            } else if (action.icon === 'edit') {
                this.showEditModal(obj);
            } else if (action.icon === 'delete') {
                this.$root.$emit('showModal', {
                    title: this.$t('automaticSentences.remove'),
                    component: Vue.component('ConfirmForm', ConfirmForm),
                    payload: { msg: this.$t('automaticSentences.confirmRemove') },
                    onConfirm: () => this.removeAutomaticSentence(obj.id),
                    isForm: false,
                });
            }
        },
        showEditModal(selectedSentence) {
            const sentence = this.sentences.find(x => x.id === selectedSentence.id);
            this.$root.$emit('showModal', {
                title: this.$t('automaticSentences.updateSentence'),
                component: Vue.component('AutomaticSentenceForm', AutomaticSentenceForm),
                showLangs: true,
                isForm: true,
                payload: { sentence: cloneDeep(sentence), isEdition: true },
                onConfirm: requestPayload => this.updateAutomaticSentence(sentence.id, requestPayload),
            });
        },
        async addAutomaticSentence(requestPayload) {
            await this.$rq.addAutomaticSentence(requestPayload);
            this.$toasted.global.toast_success({
                message: this.$t('msg.categoryAdded'),
            });
            this.resetPaginationVariables();
            await this.getSentences();
        },
        async updateAutomaticSentence(id, requestPayload) {
            if (!requestPayload) return;
            await this.$rq.updateAutomaticSentence(id, requestPayload);
            this.$toasted.global.toast_success({
                message: this.$t('msg.categoryModified'),
            });
            this.resetPaginationVariables();
            await this.getSentences();
        },
        async removeAutomaticSentence(id) {
            await this.$rq.removeAutomaticSentence(id);
            this.$toasted.global.toast_success({
                message: this.$t('msg.categoryRemoved'),
            });
            this.resetPaginationVariables();
            await this.getSentences();
        },
        async onChangeSwitchFilters(newFilters, queryParams) {
            const [includeInactive] = newFilters;

            this.queryParams = {
                ...this.queryParams,
                ...queryParams,
                includeInactive: includeInactive.value,
                page: 1,
            };
            this.resetPaginationVariables();
            await this.getSentences();
        },
        displayedText(content) {
            return content[this.userLang] && content[this.userLang].length
                ? content[this.userLang] : content[this.teamMainLang];
        },
        // each time we switch from list scrolling to filtering or searching we need to reset pagination elements
        // to avoid having duplicate list and be sure we have all data with new consition
        resetPaginationVariables() {
            this.pagination = {};
            this.sentences = [];
            this.queryParams.page = 1;
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition) {
                this.queryParams.page = this.pagination.currentPage + 1;
                // access "v-autocomplete" ref
                await this.getSentences();
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                // workaround to refresh list after adding new items.
                    ref.onScroll();
                }
            }
        },
    },
};
</script>
<style>
    .d-flex{
        display: flex;
    }
    .center{
        justify-content: center;
    }
</style>
